import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { helper } from "@/lib/helper";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, BadgeCheck, Banknote, Calendar, Hash, Landmark, Loader2, Pencil, Receipt, User2, Wallet } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { RowActionMenu } from "@/components/RowActionMenu";
import { cn } from "@/lib/utils";

type CustomerLoanRow = {
  transaction_code?: string;
  status_text?: string;
  application_date?: string;
  disbursed_date?: string;
  id?: number | string;
  loan_amount?: number | string;
  joinCharged?: number | string;
  applicationCharged?: number | string;
  interest_charged?: number | string;
  adminCharged?: number | string;
  insuranceCharged?: number | string;
  penalty?: number | string;
  paid?: number | string;
  balance?: number | string;
  due_date?: string;
  USERNAME?: string;
  USERNAME2?: string;
};



type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

export default function CustomerLoans({ customerId }: { customerId: string }) {
  const perPage = 10;
  const [err, setErr] = useState("");
  const location = useLocation();
  const nonce = (location.state as any)?.nonce;
  /** paginated loans */
  const [loans, setLoans] = useState<CustomerLoanRow[]>([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [loansMeta, setLoansMeta] = useState<PaginationMeta | null>(null);
  const [loansPage, setLoansPage] = useState(1);


  /** API fetchers */
  async function fetchLoans(page = 1) {
    if (!customerId) return;
    if (loansLoading) return;

    setLoansLoading(true);
    try {
      const res = await apiFetch<any>(`/customer-profile/${customerId}/loans?page=${page}&per_page=${perPage}`);
      const rows: CustomerLoanRow[] = res?.data?.data ?? res?.data ?? [];
      const meta: PaginationMeta | null = res?.data?.meta ?? null;

      setLoans(rows); // page-style pagination (replace)
      setLoansMeta(meta);
      setLoansPage(page);
    } catch (e: any) {
      setErr(e?.message || "Failed to load loans");
    } finally {
      setLoansLoading(false);
    }
  }

  function loanBdoName(r: any) {
    return [r?.USERNAME, r?.USERNAME2].filter(Boolean).join(" ").trim() || "-";
  }

  function loanInterestTotal(r: any) {
    return (
      Number(r?.interest_charged || 0) +
      Number(r?.adminCharged || 0) +
      Number(r?.insuranceCharged || 0)
    );
  }

  function statusPill(status?: string) {
    const s = (status || "").toLowerCase();
    if (s.includes("active")) return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
    if (s.includes("default")) return "bg-red-500/10 text-red-700 border-red-200";
    if (s.includes("closed")) return "bg-slate-500/10 text-slate-700 border-slate-200";
    if (s.includes("pending")) return "bg-amber-500/10 text-amber-700 border-amber-200";
    if (s.includes("reject")) return "bg-rose-500/10 text-rose-700 border-rose-200";
    return "bg-primary/10 text-primary border-primary/20";
  }

  useEffect(() => {
    fetchLoans()
  }, [nonce]);



  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  return (
    <>

      {/* Loans */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold">Customer Loans</div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => alert("TODO: view all loans")}>
            View all loans
          </Button>
          <DropdownMenu>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => alert("TODO: download loans")}>Download Loans</DropdownMenuItem>
              <DropdownMenuItem onClick={() => alert("TODO: download payments")}>Download Payments</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">Details</th>
              <th className="p-3">Reference</th>
              <th className="p-3">Date</th>
              <th className="p-3">Loan ID</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Loan Fees</th>
              <th className="p-3">Paid</th>
              <th className="p-3">Due</th>
              <th className="p-3">Due Date</th>
              <th className="p-3">BDO</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loansLoading ? (
              <tr>
                <td className="p-6 text-muted-foreground" colSpan={11}>
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading loans...
                  </div>
                </td>
              </tr>
            ) : loans.length ? (
              loans.map((r, idx) => (
                <tr key={idx} className="border-t">
                  <td className="p-3">
                    <span className="text-primary underline">{helper.safe(r.status_text, "Active")}</span>
                  </td>
                  <td className="p-3">{helper.safe(r.transaction_code)}</td>
                  <td className="p-3">
                    <div className="text-xs text-muted-foreground">App.Date: {helper.safe(r.application_date)}</div>
                    <div className="text-xs text-muted-foreground">Dis.Date: {helper.safe(r.disbursed_date)}</div>
                  </td>
                  <td className="p-3 text-primary underline">{helper.safe(r.id)}</td>
                  <td className="p-3">{helper.nf(r.loan_amount)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge variant="secondary">Join: {helper.nf(r.joinCharged)}</Badge>
                      <Badge variant="secondary">Application: {helper.nf(r.applicationCharged)}</Badge>
                      <Badge variant="secondary">Interest: {helper.nf(loanInterestTotal(r))}</Badge>
                      <Badge variant="secondary">Penalty: {helper.nf(r.penalty)}</Badge>
                    </div>
                  </td>
                  <td className="p-3">{helper.nf(r.paid)}</td>
                  <td className="p-3">{helper.nf(r.balance)}</td>
                  <td className="p-3">{helper.safe(r.due_date)}</td>
                  <td className="p-3">{loanBdoName(r)}</td>
                  <td className="p-3">
                     <RowActionMenu
                                    isRTL={isRTL}
                                   
                                  />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={11}>
                  No loan rows yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loansLoading ? (
          <Card className="shadow-card">
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading loans...
            </CardContent>
          </Card>
        ) : loans.length ? (
          loans.map((r, idx) => (
            <Card
              key={idx}
              className="shadow-card rounded-2xl border bg-background hover:shadow-md transition-shadow"
            >
              <CardContent className="p-4 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
                          statusPill(helper.safe(r.status_text, "Active"))
                        )}
                      >
                        {helper.safe(r.status_text, "Active")?.toLowerCase().includes("default") ? (
                          <AlertTriangle className="h-3.5 w-3.5" />
                        ) : (
                          <BadgeCheck className="h-3.5 w-3.5" />
                        )}
                        {helper.safe(r.status_text, "Active")}
                      </span>

                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Receipt className="h-3.5 w-3.5" />
                        Ref:
                        <span className="text-foreground font-semibold truncate max-w-[180px]">
                          {helper.safe(r.transaction_code)}
                        </span>
                      </span>
                    </div>

                    {/* Dates */}
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                        App: <span className="text-foreground">{helper.safe(r.application_date)}</span>
                      </span>
                      <span className="opacity-60">•</span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                        Disb: <span className="text-foreground">{helper.safe(r.disbursed_date)}</span>
                      </span>
                    </div>
                  </div>

                  {/* Loan ID + actions */}
                  <div className="flex items-start gap-2 shrink-0">
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/60 border text-xs font-semibold">
                      <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                      {helper.safe(r.id)}
                    </div>

                    <RowActionMenu isRTL={isRTL} />
                  </div>
                </div>

                {/* Money metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <Metric
                    icon={Banknote}
                    label="Amount"
                    value={helper.nf(r.loan_amount)}
                  />
                  <Metric
                    icon={Wallet}
                    label="Balance"
                    value={helper.nf(r.balance)}
                  />
                  <Metric
                    icon={ArrowUpRight}
                    label="Paid"
                    value={helper.nf(r.paid)}
                  />
                  <Metric
                    icon={ArrowDownRight}
                    label="Due Date"
                    value={helper.safe(r.due_date)}
                  />
                </div>

                {/* Fee chips */}
                <div className="flex flex-wrap gap-2">
                  <Chip icon={Landmark} label="Join" value={helper.nf(r.joinCharged)} />
                  <Chip icon={Receipt} label="Application" value={helper.nf(r.applicationCharged)} />
                  <Chip icon={Banknote} label="Interest" value={helper.nf(loanInterestTotal(r))} />
                  <Chip icon={AlertTriangle} label="Penalty" value={helper.nf(r.penalty)} />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1 min-w-0">
                    <User2 className="h-3.5 w-3.5 text-primary" />
                    BDO:
                    <span className="text-foreground font-semibold truncate">{loanBdoName(r)}</span>
                  </div>

                  <div className="text-[11px] text-muted-foreground">
                    Updated: <span className="text-foreground">{helper.safe(r.disbursed_date, "-")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-sm text-muted-foreground">No loan rows yet.</div>
        )}
      </div>

      {/* Pagination (loans) */}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {loansMeta ? (
            <>
              Page {loansMeta.current_page} of {loansMeta.last_page} • Total {loansMeta.total}
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={loansLoading || loansPage <= 1}
            onClick={() => fetchLoans(Math.max(1, loansPage - 1))}
            className="gap-2"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            disabled={loansLoading || !(loansMeta?.has_more ?? false)}
            onClick={() => fetchLoans(loansPage + 1)}
            className="gap-2"
          >
            {loansLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Next
          </Button>
          <Button variant="secondary" disabled={loansLoading} onClick={() => fetchLoans(1)} className="gap-2">
            {loansLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </div>
      </div>
    </>
  );

  /** --- Reusable little components --- */
  function Metric({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: string;
  }) {
    return (
      <div className="rounded-xl border bg-muted/20 p-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
            <div className="text-sm font-semibold truncate">{value}</div>
          </div>
        </div>
      </div>
    );
  }

  function Chip({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: string;
  }) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border bg-background">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground font-medium">{label}:</span>
        <span className="font-semibold">{value}</span>
      </span>
    );
  }

}